import React, { useState, useEffect, useRef } from 'react';
import { CampaignQuestionType, CampaignType, CampaignTypeText, StepProps } from '../types';
import Loading from 'Common/Components/Loading';

interface CampaignSetupStepProps extends StepProps {
  questionAnswer: any[];
  showErrorForm: boolean;
  setShowErrorForm: (show: boolean) => void;
  isView: boolean;
  reviewLink?: string;
}

const CampaignSetupStep: React.FC<CampaignSetupStepProps> = ({
  formik,
  questionAnswer,
  showErrorForm,
  setShowErrorForm,
  isView,
  reviewLink,
}) => {
  const [showOtherInputs, setShowOtherInputs] = useState([false, false, false]);
  const [shouldShowColdEmailFields, setShouldShowColdEmailFields] = useState(false);
  const [customAnswers, setCustomAnswers] = useState(['', '', '']);
  const [coldEmailContent, setColdEmailContent] = useState('');
  // const [coldEmailRepeat, setColdEmailRepeat] = useState(1);
  const [coldEmailLimit, setColdEmailLimit] = useState(1);
  const [emailsSendingTime, setEmailsSendingTime] = useState<{ index: number; name: string; sendAfter: number }[]>([]);
  const isInitializedRef = useRef(false);

  // Handle custom answer visibility for each question
  useEffect(() => {
    const newShowOtherInputs = [...showOtherInputs];
    const newCustomAnswers = [...customAnswers];

    setShouldShowColdEmailFields(false);

    formik.values.answers.forEach((answer: any, index: number) => {
      const currentAnswer = answer?.answerText || '';
      const isRadioOption = questionAnswer?.find((qa: any) =>
        qa.questionText === answer.questionText
      )?.answers.some((option: any) => option.answerText === currentAnswer);

      if (!isRadioOption && currentAnswer) {
        newCustomAnswers[index] = currentAnswer;
        newShowOtherInputs[index] = true;
      }
    });

    if (formik.values.answers.some((answer: any) => {
      const currentAnswer = answer?.answerText || '';
      return answer.questionText.includes(CampaignQuestionType.Type) && currentAnswer === CampaignTypeText.ColdEmailOutreach;
    })) {
      setShouldShowColdEmailFields(true);
    }

    setCustomAnswers(newCustomAnswers);
    setShowOtherInputs(newShowOtherInputs);
  }, [formik.values.answers, questionAnswer]);

  // Initialize cold email fields from formik values only once on mount
  useEffect(() => {
    if (true) {
      if (formik.values.coldEmail?.coldEmailContent !== undefined) {
        setColdEmailContent(formik.values.coldEmail.coldEmailContent);
      }
      if (formik.values.coldEmail?.coldEmailLimit !== undefined) {
        setColdEmailLimit(formik.values.coldEmail.coldEmailLimit);
      }
      if (formik.values.coldEmail?.emailsSendingTime !== undefined) {
        setEmailsSendingTime(formik.values.coldEmail.emailsSendingTime);
      }
      // isInitializedRef.current = true;
    }
  }, [formik.values.coldEmail?.coldEmailContent, formik.values.coldEmail?.coldEmailRepeat, formik.values.coldEmail?.coldEmailLimit, formik.values.coldEmail?.emailsSendingTime]);

  // Update emailsSendingTime when coldEmailLimit changes
  useEffect(() => {
    if (coldEmailLimit > 0) {
      const newEmailsSendingTime = [];
      for (let i = 0; i < coldEmailLimit; i++) {
        const existing = emailsSendingTime.find(item => item.index === i);
        if (existing) {
          newEmailsSendingTime.push(existing);
        } else {
          newEmailsSendingTime.push({
            index: i,
            name: getEmailName(i),
            sendAfter: i > 0 ? 1 : 0,
          });
        }
      }
      setEmailsSendingTime(newEmailsSendingTime);
      formik.setFieldValue('coldEmail', {
        coldEmailContent: coldEmailContent,
        coldEmailLimit: coldEmailLimit,
        emailsSendingTime: newEmailsSendingTime
      });
    } else {
      formik.setFieldValue('coldEmail', {
        coldEmailContent: coldEmailContent,
        coldEmailLimit: coldEmailLimit,
        emailsSendingTime: emailsSendingTime
      });
    }
  }, [, coldEmailLimit, emailsSendingTime.length]);

  const getEmailName = (index: number) => {
    const names = ['First', 'Second', 'Third', 'Fourth', 'Fifth'];
    return names[index] ? `${names[index]} email` : `${index + 1}th email`;
  };

  const handleSendAfterChange = (index: number, value: number) => {
    const newEmailsSendingTime = emailsSendingTime.map(item =>
      item.index === index ? { ...item, sendAfter: value } : item
    );
    setEmailsSendingTime(newEmailsSendingTime);
    formik.setFieldValue('coldEmail', {
      coldEmailContent: coldEmailContent,
      coldEmailLimit: coldEmailLimit,
      emailsSendingTime: newEmailsSendingTime
    });
  };


  const handleRadioChange = (questionIndex: number, value: string) => {
    const fieldName = `answers[${questionIndex}].answerText`;
    formik.setFieldValue(fieldName, value);
    setShowErrorForm(false);

    const newShowOtherInputs = [...showOtherInputs];
    newShowOtherInputs[questionIndex] = false;
    setShowOtherInputs(newShowOtherInputs);

    const question = formik.values.answers[questionIndex];
    if (question?.questionText.includes(CampaignQuestionType.Type)) {
      if (value === CampaignTypeText.ColdEmailOutreach) {
        setShouldShowColdEmailFields(true);
      } else {
        setShouldShowColdEmailFields(false);
      }
    }
  };

  const handleOtherSelect = (questionIndex: number) => {
    const newShowOtherInputs = [...showOtherInputs];
    newShowOtherInputs[questionIndex] = true;
    setShowOtherInputs(newShowOtherInputs);

    const fieldName = `answers[${questionIndex}].answerText`;
    formik.setFieldValue(fieldName, customAnswers[questionIndex]);
    setShowErrorForm(false);
  };

  const handleCustomAnswerChange = (questionIndex: number, value: string) => {
    const fieldName = `answers[${questionIndex}].answerText`;
    formik.setFieldValue(fieldName, value);

    const newCustomAnswers = [...customAnswers];
    newCustomAnswers[questionIndex] = value;
    setCustomAnswers(newCustomAnswers);
    setShowErrorForm(false);
  };

  const handleColdEmailContentChange = (value: string) => {
    setColdEmailContent(value);
    formik.setFieldValue('coldEmail', {
      coldEmailContent: value,
      coldEmailLimit: coldEmailLimit
    });
  };

  const handleColdLimitChange = (value: number) => {
    setColdEmailLimit(value);
    formik.setFieldValue('coldEmail', {
      coldEmailContent: coldEmailContent,
      coldEmailLimit: value
    });
  };

  const renderQuestion = (questionIndex: number) => {
    const question = formik.values.answers[questionIndex];
    const questionData = questionAnswer?.find((qa: any) => qa.questionText === question?.questionText);
    const currentAnswer = question?.answerText || '';
    const fieldName = `answers[${questionIndex}].answerText`;

    const isRadioOption = questionData?.answers.some((option: any) => option.answerText === currentAnswer);
    const isOtherSelectedWithoutText = (showOtherInputs[questionIndex] || (!isRadioOption && currentAnswer === '')) && currentAnswer === '';

    return (
      <div key={questionIndex} className="pb-2 mb-2">
        <h6 className="font-medium mb-1 text-[13px] text-gray-700">
          Question {questionIndex + 1}: {question?.questionText}
        </h6>

        <div className="space-y-3">
          {questionData?.answers.map((option: any) => (
            <div key={option.id}>
              <label className={`flex items-start gap-2 ${isView ? 'cursor-default' : 'cursor-pointer'}`}>
                <input
                  type="radio"
                  name={fieldName}
                  value={option.answerText}
                  onChange={(e) => handleRadioChange(questionIndex, e.target.value)}
                  checked={currentAnswer === option.answerText}
                  disabled={isView}
                  className="mt-1 flex-shrink-0"
                />
                <span className="text-sm sm:text-base">{option.answerText}</span>
              </label>
              {option.answerText === 'Request for review' && currentAnswer === option.answerText && (!reviewLink || reviewLink.trim() === '') && (
                <div className="text-red-500 text-xs sm:text-sm mt-2 ml-6">
                  This campaign type requires a Review Form. Please add your form link on setting page to continue.
                </div>
              )}

              {option.answerText === 'Cold email outreach' && shouldShowColdEmailFields && question?.questionText.includes('What type of campaign strategies?') && (
                <div className="mt-4 ml-6 space-y-4 border-l-2 border-blue-300 pl-4">
                  {/* Content Field */}
                  <div className="space-y-2">
                    <label className="font-bold block text-sm text-gray-700">
                      Email Topic & Content
                    </label>
                    <textarea
                      value={coldEmailContent}
                      onChange={(e) => handleColdEmailContentChange(e.target.value)}
                      placeholder="Describe the main topic and what the email should communicate."
                      rows={4}
                      disabled={isView}
                      className="w-full border rounded-md form-textarea border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200 resize-vertical text-sm px-3 py-2"
                    />
                  </div>


                  <div className='flex gap-2 justify-between grid-cols-2'>
                    {/* Limit Field */}
                    <div className="space-y-2 flex-1">
                      <label className="block text-sm font-bold text-gray-700">
                        Number of Emails to Generate
                      </label>
                      <input
                        max={5}
                        type="number"
                        min="1"
                        value={coldEmailLimit || 1}
                        onChange={(e) => handleColdLimitChange(Number(e.target?.value))}
                        disabled={isView}
                        placeholder="Enter submission limit"
                        className="w-full border rounded-md form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200 text-sm px-3 py-2"
                      />
                    </div>
                  </div>
                  {/* set email sending time */}
                  {emailsSendingTime.length > 0 && (
                    <div className="space-y-2">
                      <label className="text-sm font-bold mb-3">
                        Email Sending Schedule Settings <span className='text-gray-700 font-normal italic text-sm'>(Set the day intervals between each email. Each email will be sent only after the previous one has been delivered.)</span>
                      </label>
                      <div className='grid grid-cols-2 gap-3'>
                        {emailsSendingTime.map((item) => (
                          <div key={item.index} className="flex items-center gap-2">
                            <label className="text-sm font-medium min-w-[85px]">
                              {item.name}
                            </label>
                            <input
                              type="number"
                              min="0"
                              value={item.sendAfter}
                              onChange={(e) => handleSendAfterChange(item.index, Number(e.target.value))}
                              disabled={isView}
                              placeholder="Days after"
                              className="w-full border rounded-md form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200 text-sm px-3 py-2"
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}

          {/* Only show "Other" option for the first question (index 0) */}
          {questionIndex === 0 && (
            <>
              <label className={`flex items-start gap-2 ${isView ? 'cursor-default' : 'cursor-pointer'} whitespace-nowrap`}>
                <input
                  type="radio"
                  name={fieldName}
                  value="other"
                  onChange={() => handleOtherSelect(questionIndex)}
                  checked={showOtherInputs[questionIndex] || (!isRadioOption && currentAnswer !== '')}
                  disabled={isView}
                  className="mt-1 flex-shrink-0"
                />
                <span className="text-sm sm:text-base">Other</span>
              </label>

              <div
                className={`transition-all duration-200 ease-linear overflow-hidden w-full ${(showOtherInputs[questionIndex] || (!isRadioOption && currentAnswer !== ''))
                  ? 'opacity-100 max-h-32'
                  : 'opacity-0 max-h-0'
                  }`}
              >
                <textarea
                  name={fieldName}
                  value={!isRadioOption ? currentAnswer : ''}
                  onChange={(e) => handleCustomAnswerChange(questionIndex, e.target.value)}
                  placeholder="Enter your custom answer here..."
                  rows={2}
                  className="w-full border rounded-md form-textarea border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200 resize-vertical text-sm sm:text-base px-3 py-2"
                  autoFocus={showOtherInputs[questionIndex] || (!isRadioOption && currentAnswer !== '')}
                  disabled={isView || !(showOtherInputs[questionIndex] || (!isRadioOption && currentAnswer !== ''))}
                />
              </div>
            </>
          )}

          {/* Error message for each question */}
          {showErrorForm && !currentAnswer && (
            <div className="text-red-500 text-xs sm:text-sm mt-2">
              {questionIndex === 0 && isOtherSelectedWithoutText
                ? "You must enter your custom answer"
                : "You must select one option"
              }
            </div>
          )}
        </div>
      </div>
    );
  };

  if (!formik.values.answers || formik.values.answers.length === 0) {
    return <>
      <div className="bg-white flex-1 flex items-center justify-center"><Loading size="sm" color="blue" isVisible={true} /></div>
    </>;
  }

  return (
    <>
      <div className='flex-1 w-full min-h-[calc(100vh-228px)] max-h-[calc(100vh-228px)] sm:min-h-[calc(100vh-218px)] sm:max-h-[calc(100vh-218px)] mx-auto shadow-sm mt-2 rounded-lg mb-4'>
        {/* Campaign Name Section */}
        <div className="space-y-4 border-b rounded-md p-4 mb-1 mt-2 bg-white">
          <h5 className="font-semibold text-sm mt-2 mb-1 sm:mb-2 text-start">
            Campaign Name
          </h5>
          <div className='flex items-center'>
            <div className="flex-1">
              <input
                disabled={isView}
                name="name"
                className="w-full form-input border-slate-200 focus:border-custom-500 disabled:bg-slate-100 disabled:border-slate-300 disabled:text-slate-500 dark:text-zink-100 placeholder:text-slate-400 text-sm sm:text-base px-3 py-2"
                placeholder="Enter Campaign Name"
                type="text"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.name || ""}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                  }
                }}
              />
              {formik.touched["name"] && typeof formik.errors["name"] === "string" ? (
                <div className="text-red-500 text-xs sm:text-sm mt-1">{formik.errors["name"] as string}</div>
              ) : null}
            </div>
          </div>
        </div>

        {/* Campaign Questions Section */}
        <div className="space-y-4 p-4 my-2 bg-white overflow-auto min-h-[200px] sm:h-[calc(100vh-350px)] h-[calc(100vh-400px)]">
          <h5 className="font-semibold mt-2 mb-1 sm:mb-3 me-auto text-start text-sm">
            Campaign Questions
          </h5>
          {formik.values.answers.map((_: any, index: number) => renderQuestion(index))}
        </div>
      </div>

    </>
  );
};

export default CampaignSetupStep;
